import {select, selection} from 'd3-selection'
// import {csv} from 'd3-dsv'
import {json} from 'd3-request'
import {scaleLinear} from 'd3-scale'
import {axisLeft, axisBottom, axisRight, axisTop} from 'd3-axis'
import {line} from 'd3-shape'

const mainContainerSelector = 'div.compression-container'
const graphContainerSelector = 'div.graph-container'
const imageContainerSelector = 'div.compressed-image'
const baseUrl = 'https://secure.stefanzero.com/static/wordpress/my-pages/image-compression/'

let getOptions = () => {
  let options = {}
  // console.log(window.location)
  if (/wordpress.stefanzero.com/.test(window.location.href)) {
    options.urlPath = `${baseUrl}output/`
  } else {
    options.urlPath = 'output/'
  }
  options.baseName = 'vegetables'
  options.cycleDeltaMs = 500
  options.$mainContainer = $(mainContainerSelector)
  options.$graphContainer = $(graphContainerSelector)
  options.$imageContainer = $(imageContainerSelector)
  options.maxWidth = 1150
  options.$mainContainer.css({ maxWidth: options.maxWidth + 'px'})
  /*
   * AspectRatio = imageWidth / imageHeight
   * The initial aspect ratio of the svg determines resized dimensions
   */
  options.imageAspectRatio = 1.5
  options.imageFraction = 0.5
  options.imageHeight = options.maxWidth * options.imageFraction / options.imageAspectRatio
  options.imageWidth = options.imageHeight * options.imageAspectRatio
  options.graphWidth = options.maxWidth - options.imageWidth
  options.graphHeight = options.imageHeight
  /*
   * The graph margins and padding are set in code, instead of CSS, because they are used for
   * calculating the svg dimensions
   */
  options.margin = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  }
  options.padding = {
    left: 60,
    right: 60,
    top: 40,
    bottom: 40
  }
  options.svgWidth = options.graphWidth - options.margin.left - options.margin.right - options.padding.left - options.padding.right
  options.svgHeight = options.graphHeight - options.margin.top - options.margin.bottom - options.padding.top - options.padding.bottom
  return options
}

let getBounds = (data) => {
  let x = data.map( (d) => d.x)
  let y = data.map( (d) => d.y)
  return {
    xMin: Math.min(...x),
    xMax: Math.max(...x),
    yMin: Math.min(...y, 0),
    yMax: roundUp(Math.max(...y))
  }
}

let roundUp = (x) => {
  /*
   * This rounds up to the next highest digit
   * To round up the second signficant digit, subtract off
   * xLog1 = Math.floor(Math.log10(x))
   * xx = x - Math.pow(10, xLog1)
   * Then
   * xLog2 = Math.ceil(Math.log10(xx))
   * xRound = Math.pow(10, xLog1) + Math.pow(10, xLog2)
   */
  const xLog = Math.floor(Math.log10(x))
  let rounded = Math.ceil(x / Math.pow(10, xLog - 1))
  rounded = rounded * Math.pow(10, xLog - 1)
  return rounded
}

let getData = function (url) {
  return new Promise((resolve, reject) => {
    json(url, (err, data) => {
      if (err) {
        reject(err)
      } else {
        resolve(data)
      }
    })
  })
}

let parseData = function (data) {
  return data.map((datum) => {
    return {
      x: datum.quality,
      y: datum.size
    }
  })
}

let createCanvas = function ($container, options) {
  $container
    .width(options.graphWidth)
    .height(options.graphHeight)

  select('.graph-container')
    .append('svg')
  var canvas =
    select('.graph-container svg')
  var width = options.graphWidth - options.margin.left - options.margin.right
  var height = options.graphHeight - options.margin.top - options.margin.bottom
  canvas
    /*
     * The next 3 lines are to allow resizing the graph with a transform
     */
    .attr("preserveAspectRatio", "xMinYMin meet")
    .attr("viewBox", `0 0 ${width} ${height}`)
    .classed("svg-content", true)
    .style('display', 'block')
    // .style('width', width)
    // .style('height', height)
    //.style('border', '2px solid blue')
    .style('margin-left', `${options.margin.left}px`)
    .style('margin-right', `${options.margin.right}px`)
    .style('margin-top', `${options.margin.top}px`)
    .style('margin-bottom', `${options.margin.bottom}px`)
  return canvas
}

/**
 *
 * @param {selection} canvas
 * @param {number} xMin
 * @param {number} xMax
 * @param {number} graphWidth
 */
let createAxis = (canvas, options, bounds) => {
  let xScale = scaleLinear()
    .domain([bounds.xMin, bounds.xMax])
    .range([0, options.svgWidth])
  const xAxisBottom = axisBottom(xScale)
  const dxBottom = options.padding.left
  // const dyBottom = options.size.height - options.margin.top - options.padding.bottom
  const dyBottom = options.svgHeight + options.padding.top
  canvas
    .append('g')
    .attr('transform', `translate(${dxBottom}, ${dyBottom})`)
    .call(xAxisBottom)
  let xAxisTop = axisTop(xScale)
  const dxTop = options.padding.left
  // const dyTop = options.margin.top
  const dyTop = options.padding.top
  canvas
    .append('g')
    .attr('transform', `translate(${dxTop}, ${dyTop})`)
    .call(xAxisTop)
  let yScale = scaleLinear()
    .domain([bounds.yMin, bounds.yMax])
    .range([options.svgHeight, 0])
  const yAxisLeft = axisLeft(yScale)
  const dxLeft = options.padding.left
  // const dyLeft = options.margin.top
  const dyLeft = options.padding.top
  canvas
    .append('g')
    .attr('transform', `translate(${dxLeft}, ${dyLeft})`)
    .call(yAxisLeft)
  let yAxisRight = axisRight(yScale)
  const dxRight = options.padding.left + options.svgWidth
  // const dyRight = options.margin.top
  const dyRight = options.padding.top
  canvas
    .append('g')
    .attr('transform', `translate(${dxRight}, ${dyRight})`)
    .call(yAxisRight)
  return {xScale, yScale}
}

/**
 *
 * @param {selection} canvas
 * @param {function} xScale
 * @param {function} yScale
 * @param {padding{left, top}} options
 * @param {Array<{x,  y}> xy
 */
let addDataLine = function(canvas, xScale, yScale, options, xy) {
  var dataLine = line()
    .x((d) => {return xScale(d.x)})
    .y((d) => {return yScale(d.y)})
  canvas
    .append('path')
    .data([xy])
    .attr('class', 'line')
    .attr('stroke', 'black')
    .attr('fill', 'none')
    .attr('transform', `translate(${options.padding.left}, ${options.padding.top})`)
    .attr('d', dataLine)
}

let createGraph = function ($container) {
  let options = getOptions()
  let canvas = createCanvas($container, options)
  let xy, bounds, xScale, yScale, obj
  const dataFile = `${options.urlPath}${options.baseName}.json`
  return new Promise((resolve, reject) => {
    getData(dataFile)
      .then((data) => {
        // console.log(data)
        xy = parseData(data)
        // console.log(xy)
        bounds = getBounds(xy)
        // xScale, yScale = createAxis(canvas, options, bounds)
        obj = createAxis(canvas, options, bounds)
        xScale = obj.xScale
        yScale = obj.yScale
        addDataLine(canvas, xScale, yScale, options, xy)
        resolve({
          options,
          canvas,
          xy,
          bounds,
          scale: {
            xScale,
            yScale
          }
        })
      })
  })
}

let addPoint = function (obj, i) {
  let { options, canvas, xy, scale } = obj
  let circle = null
  if ((i >= 0) && (i < xy.length)) {
    const xValue = xy[i].x
    const yValue = xy[i].y
    const x = scale.xScale(xValue) + options.padding.left
    const y = scale.yScale(yValue) + options.padding.top
    const className = `circle-${i}`
    let pointGroup =
      canvas
        .selectAll('.point-group')
    if (pointGroup.empty()) {
      pointGroup =
        canvas
          .append('g')
          .attr('class', 'point-group')
    }

    /*
    canvas
      .append('g')
      .attr('class', 'point-group')
    */
    // console.log(x, y)
    pointGroup
      .append('circle')
      .data([y])
      .attr('id', className)
      .attr('class', className)
      .attr('r', 10)
      .attr('cx', x)
      .attr('cy', y)
      .attr('fill', 'red')
    circle = canvas
      .select(`circle .${className}`)
  }
  return circle
}

let removePoint = (obj, index) => {
  let { options, canvas, xy, scale } = obj
  /** @type {selection} */
  let point = canvas
    .select('.point-group')
    .select(`.circle-${index}`)
  point
    .remove()
}

let addImage = (obj, pointNumber) => {
  const options = obj.options
  const $imageDiv = $('.compressed-image')
  pointNumber = `${pointNumber}`
  if (pointNumber < 10) {
    pointNumber = '0' + pointNumber
  }
  const useBackgroundImage = false
  if (useBackgroundImage) {
    const url = `${options.urlPath}${options.baseName}.${pointNumber}.jpg`
    $imageDiv.css({
      height: options.graphHeight + 'px',
      width: options.graphHeight * 1.5 + 'px',
      backgroundImage: `url(${url})`
    })
  } else {
    $imageDiv.html('')
    const image = obj.images[pointNumber]
    // console.log(image.src)
    $imageDiv[0].appendChild(image)
    /*
     * Set size of image
     */
    let containerWidth = options.$mainContainer.width()
    sizeElements(containerWidth, options)
  }
}

let addButtonHandler = (obj) => {
  let points = new Array(20).fill(0).map(
    function (val, index) {
      return (index + 1) * 5 - 1
    })
  obj.points = points
  const options = obj.options
  const promises = cacheImages(obj)
  let index = 0
  let pointNumber = points[index]
  addPoint(obj, pointNumber)
  promises[0].then(() => {
    addImage(obj, pointNumber)
    /*
     * Set size of initial image
     */
    let containerWidth = options.$mainContainer.width()
    sizeElements(containerWidth, options)
  })
  const button1 = document.getElementById('button1')
  button1.innerHTML = 'Pause'
  var paused = false
  let cyclePoint = () => {
    removePoint(obj, pointNumber)
    index = index === points.length - 1 ? 0 : ++index
    // console.log(index, obj.points[index])
    pointNumber = points[index]
    addPoint(obj, pointNumber)
    addImage(obj, pointNumber)
  }
  let timerId
  let startCycleTimer = () => {
    timerId = setInterval(() => {
      cyclePoint()
    }, options.cycleDeltaMs)
    return timerId
  }
  Promise.all(promises).then(() => {
    button1.addEventListener('click', (evt) => {
      if (paused) {
        timerId = startCycleTimer()
        button1.innerHTML = 'Pause'
      } else {
        window.clearInterval(timerId)
        button1.innerHTML = 'Resume'
      }
      paused = ! paused
    })
    timerId = startCycleTimer()
  })
}

/**
 *
 * @param obj
 * @returns {Array<Promise>}
 */
let cacheImages = (obj) => {
  const promises = []
  const options = obj.options
  obj.images = {}
  obj.points.forEach((point) => {
    promises.push(
      new Promise( (resolve, reject) => {
        const image = new Image()
        image.width = options.imageWidth
        image.height = options.imageHeight
        image.alt = 'Venice Vegetable Market'
        point = point < 10 ? `0${point}` : `${point}`
        obj.images[point] = image
        const url = `${options.urlPath}${options.baseName}.${point}.jpg`
        // console.log(url)
        image.onload = resolve
        image.src = url
      })
    )
  })
  return promises
}

let initGraph = () => {
  const $graphContainer = $(graphContainerSelector)
  return new Promise( (resolve, reject) => {
    createGraph($graphContainer).then((obj) => {
      // console.log(obj)
      addButtonHandler(obj)
      resolve()
    })
  })
}


let sizeElements = (currentWidth, options) => {
  options.imageHeight = currentWidth * options.imageFraction / options.imageAspectRatio
  options.imageWidth = options.imageHeight * options.imageAspectRatio
  options.graphWidth = currentWidth - options.imageWidth
  options.graphHeight = options.imageHeight
  const $image = options.$imageContainer.find('img')
  if ($image.length) {
    const image = $image[0]
    image.height = options.imageHeight
    image.width = options.imageWidth
    image.style.height = options.imageHeight + 'px'
  }
  options.$graphContainer.css({
    width: options.graphWidth + 'px',
    height: options.graphHeight + 'px'
  })
}


let getHorizontalBorders = ($element) => {
  const leftBorder = parseInt($element.css('border-left'))
  const rightBorder = parseInt($element.css('border-right'))
  return leftBorder + rightBorder
}


initGraph().then( () => {
  const options = getOptions()
  // let $body = $('body')
  let lastContainerWidth = options.$mainContainer.width()
  sizeElements(lastContainerWidth, options)
  let resizeContainer = () => {
    let currentContainerWidth = options.$mainContainer.width()
    if (currentContainerWidth !== lastContainerWidth) {
      sizeElements(currentContainerWidth, options)
      lastContainerWidth = currentContainerWidth
    }
  }
  window.addEventListener('resize', resizeContainer)
})




// WEBPACK FOOTER //
// ./src/application.js